<script lang="tsx">
  import {
    defineComponent, PropType, computed,
    provide, ref,
    watch,
  } from 'vue'
  import { BeerStyleObj } from '@xhs/yam-beer/core'
  import NewBsBox from '@xhs/yam-beer/cmp/primitive/NewBsBox'
  import { StepStatus } from '@xhs/yam-beer/cmp/constants'
  import NumberStep from './NumberStep.vue'
  import { getCurrentStatus } from './utils'
  import { BeerStepItem } from './BeerSteps.d'

  /**
   * 步骤指引组件
   */
  export default defineComponent({
    name: 'BeerSteps',
    props: {
      /**
       * @ignore
       * 容器 BeerStyle
       */
      bs: {
        type: Object as PropType<BeerStyleObj>,
        default: () => ({}),
      },
      /**
       * 步骤配置列表，会被传入 NumberSteps props，需要 title, content 属性。
       * 可选 status 属性，可用来配置错误步骤
       *
       * @see 例子：使用快捷配置项 #使用快捷配置项
       */
      list: {
        type: Array as PropType<BeerStepItem[]>,
        required: false,
      },
      /**
       * @deprecated 4.1.0
       *
       * 请使用 list
       */
      lst: {
        type: Array as PropType<BeerStepItem[]>,
        default: () => [],
      },
      /**
       * 起始序号，默认从 0 开始算
       * 会影响显示的数值和 currentStep
       */
      baseIndex: {
        type: Number,
        default: 0,
      },
      /**
       * 当前步骤，从 baseIndex 开始计
       */
      currentStep: {
        type: Number,
        default: -1,
      },
      /**
       * 步骤配置列表的情况下统一的 click 回调
       * @param {number} stepNo 点击的步骤
       */
      onClickStep: Function,
    },
    setup(props, { slots, attrs }) {
      const stepList = ref(props.list || props.lst)
      watch(() => props.list || props.lst, val => {
        stepList.value = val
      })
      provide('step', {
        addStep(item: BeerStepItem) {
          stepList.value = stepList.value.concat([item])
          return stepList.value.length - 1 + props.baseIndex
        },
        removeStep(title?: string) {
          stepList.value = stepList.value.filter(i => i.title !== title)
        },
        currentStep: computed(() => props.currentStep + props.baseIndex),
        onClickStep: props.onClickStep,
        progressDot: slots.progressDot,
      })
      const computedBs = computed(() => ({
        'cmp.steps': 'container',
        ...props.bs,
      }))
      return () => {
        const vnodes = slots.default?.() || []
        const { currentStep } = props
        if (vnodes.length > 0) {
          return (
            <NewBsBox bs={computedBs.value}>
              {vnodes.map((vnode, index) => {
                const isActive = currentStep >= index
                return [
                  index > 0 ? (
                    <NewBsBox
                      bs={ { 'cmp.steps.number': 'connector', bg: isActive ? 'primary' : 'outline' }}
                      data-active={isActive}
                    />
                  ) : null,
                  vnode,
                ]
              })}
            </NewBsBox>
          )
        }

        return (
          <NewBsBox bs={computedBs.value} {...attrs}>
            {stepList.value.map((step, index) => {
              const { status, ...rest } = step
              const currentStatus = getCurrentStatus(status, index, currentStep)
              // eslint-disable-next-line max-len
              const isActive = ([StepStatus.DONE, StepStatus.PROGRESS] as StepStatus[]).includes(currentStatus)
              return [
                index > 0 ? (
                  <NewBsBox
                    bs={ { 'cmp.steps.number': 'connector', bg: isActive ? 'primary' : 'outline' }}
                    data-active={isActive}
                  />
                ) : null,
                vnodes[index] || <NumberStep
                  status={currentStatus}
                  stepNo={index + props.baseIndex}
                  isFromConfig={true}
                  {...rest}
                />,
              ]
            })}
          </NewBsBox>
        )
      }
    },
  })
</script>
